import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Spin, Tag, Input, Button, Form, Select, Radio } from "antd";
import {
  MedicineBoxOutlined,
  DeploymentUnitOutlined,
  UserOutlined,
  PlayCircleOutlined,
  FireOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import ModelWrapper from "../../../modal/ModelWrapper";
import {
  CustomButton,
  CustomSecondaryButton,
} from "../../physician_page/styled";
import journeyService from "../../../../services/journey";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import { SpecilityContext } from "../../../../context/SpecilityContext";
import { notify } from "../../../../utils/helper";
import ViewVideoModal from "./ViewVideoModal";

const ModalContent = styled.div`
  padding: 10px;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleText = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const SubtitleText = styled.span`
  font-size: 14px;
  color: var(--secondary-text-color);
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
  margin-bottom: 12px;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const StepItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background-color: var(--secondary-bg);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #8a3ef4;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const StepMeta = styled.div`
  font-size: 12px;
  color: var(--secondary-text-color);
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const OverviewCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background-color: var(--primary-bg);
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
  &:hover {
    border-color: #8a3ef4;
    box-shadow: 0 8px 24px rgba(138, 62, 244, 0.12);
    transform: translateY(-1px);
  }
`;

const OverviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const OverviewHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OverviewHeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OverviewHeaderSubtitle = styled.div`
  font-size: 14px;
  color: var(--secondary-text-color);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InfoStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
`;

const InfoStatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const StatIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background-color: transparent;
  color: var(--secondary-text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const InfoStatLabel = styled.div`
  font-size: 13px;
  color: var(--secondary-text-color);
  font-weight: 500;
`;

const InfoStatValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--primary-text-color);
  opacity: 0.95;
  background-color: var(--secondary-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.4;
`;

const FormContainer = styled.div`
  .ant-form-item {
    margin: 0 0 20px;
  }
`;

const ViewTemplatesModal = ({
  isOpen,
  setOpen,
  templateData,
  setTemplateData,
  onUseTemplate,
  isEdit,
  setIsEdit,
  fetchTemplates,
}) => {
  const [loading, setLoading] = useState(false);
  const { specialtyOptions } = useContext(SpecilityContext);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedStep, setSelectedStep] = useState([]);

  const getStepTitle = (step) => {
    return (
      capitalizeFirstLetter(step?.title) ||
      capitalizeFirstLetter(step?.video?.title) ||
      "Video"
    );
  };

  const getStepMeta = (step) => {
    const duration = step?.video?.duration;
    if (!duration) return "";
    const minutes = Math.floor(duration / 60);
    const secs = duration % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )} min`;
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setTemplateData([]);
  };

  const handleSave = async (values) => {
    const id = templateData?.id || templateData?._id;
    if (!id) return;
    try {
      setLoading(true);
      const payload = {
        name: values.title,
        description: values.description,
        condition: values.condition,
        specialty: values.specialty,
        type: values.type,
      };
      const res = await journeyService.updateTemplate(id, payload);
      notify("success", "Template Updated Successfully")
      setOpen(false);
      setIsEdit(false);
      setTemplateData([]);
      setLoading(false);
      fetchTemplates();
    } catch (err) {
        setLoading(false);
    }
  };

  const [form] = Form.useForm();
  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        title: templateData?.name || "",
        description: templateData?.description || "",
        condition: templateData?.condition || "",
        specialty: templateData?.specialty || "N/A" || "",
        type: templateData?.type || "draft",
      });
    } else {
      form.setFieldsValue({
        title: "",
        description: "",
        condition: "",
        specialty: undefined,
        type: "draft",
      });
    }
  }, [templateData, form, isEdit]);

  const playVideo = (step) => {
    setSelectedStep(step);
    setShowVideo(true);
  };

  return (
    <>
    {showVideo && (
      <ViewVideoModal
        isOpen={showVideo}
        setOpen={setShowVideo}
        videoData={selectedStep}
        journeyData={templateData}
        fetchJourney={fetchTemplates}
        templateView={true}
      />
    )}
    <ModelWrapper
      show={isOpen}
      setShow={handleClose}
      title={
        isEdit ? (
          <HeaderTitle>
            <TitleText>Edit Template</TitleText>
            <SubtitleText>Edit the template details</SubtitleText>
          </HeaderTitle>
        ) : (
          <HeaderTitle>
            <TitleText>Template Preview</TitleText>
            <SubtitleText>Review template details and steps</SubtitleText>
          </HeaderTitle>
        )
      }
      footer={null}
      width={700}
      keyClose={false}
      onCancel={handleClose}
      className="custom-detail-modal"
    >
      <ModalContent>
        {templateData &&
          (isEdit ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              loading={loading}
            >
              <FormContainer>
                <Row gutter={[24]}>
                  <Col span={12}>
                    <Form.Item name="title" label="Title">
                      <Input
                        placeholder="Enter title"
                        size="large"
                        style={{
                          borderRadius: "6px",
                          backgroundColor: "var(--secondary-bg)",
                          border: "1px solid var(--border-color)",
                          width: "100%",
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="type" label="Type">
                      <Radio.Group>
                        <Radio value="draft">Draft</Radio>
                        <Radio value="public">Public</Radio>
                        <Radio value="private">Private</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="condition" label="Condition">
                      <Input
                        placeholder="Enter condition"
                        size="large"
                        style={{
                          borderRadius: "6px",
                          backgroundColor: "var(--secondary-bg)",
                          border: "1px solid var(--border-color)",
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="specialty" label="Specialty">
                      <Select
                        getPopupContainer={(triggerNode) =>
                          triggerNode.parentNode
                        }
                        placeholder="Select Medical Specialty"
                        showSearch
                        style={{ width: "100%" }}
                        dropdownStyle={{
                          maxHeight: 200,
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                        listHeight={160}
                        options={specialtyOptions}
                        size="large"
                        loading={loading}
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item name="description" label="Description">
                      <Input.TextArea
                        placeholder="Enter description"
                        rows={3}
                        style={{
                          borderRadius: "6px",
                          backgroundColor: "var(--secondary-bg)",
                          border: "1px solid var(--border-color)",
                          marginTop: "8px",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={24} className="d-flex justify-end gap-12">
                    <CustomSecondaryButton onClick={handleClose}>
                      Cancel
                    </CustomSecondaryButton>
                    <CustomButton htmlType="submit">
                      Update Template
                    </CustomButton>
                  </Col>
                </Row>
              </FormContainer>
            </Form>
          ) : (
            <>
              {<SectionTitle>Overview</SectionTitle>}
              <OverviewCard>
                <OverviewHeader>
                  <OverviewHeaderRow>
                    <OverviewHeaderTitle>
                      {capitalizeFirstLetter(templateData?.name)}
                    </OverviewHeaderTitle>
                    {templateData?.type && (
                      <Tag
                        style={{
                          marginLeft: "4px",
                          backgroundColor: "#F3E8FF",
                          color: "#8a3ef4",
                          border: "1px solid #8a3ef4",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "0px 8px",
                          borderRadius: "16px",
                          textTransform: "capitalize",
                        }}
                      >
                        {capitalizeFirstLetter(templateData?.type)}
                      </Tag>
                    )}
                  </OverviewHeaderRow>
                  <OverviewHeaderSubtitle>
                    {capitalizeFirstLetter(templateData?.description)}
                  </OverviewHeaderSubtitle>
                </OverviewHeader>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <MedicineBoxOutlined />
                        </StatIcon>
                        <InfoStatLabel>Condition</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {templateData?.condition || "N/A"}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <DeploymentUnitOutlined />
                        </StatIcon>
                        <InfoStatLabel>Specialty</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {templateData?.specialty || "N/A"}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <UserOutlined />
                        </StatIcon>
                        <InfoStatLabel>Created By</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {templateData?.createdBy?.name || "Unknown"}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <FieldTimeOutlined />
                        </StatIcon>
                        <InfoStatLabel>Created</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {templateData?.createdAt
                          ? new Date(
                              templateData.createdAt
                            ).toLocaleDateString()
                          : "N/A"}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <PlayCircleOutlined />
                        </StatIcon>
                        <InfoStatLabel>Videos</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {(templateData?.steps || []).length}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InfoStat>
                      <InfoStatHeader>
                        <StatIcon>
                          <FireOutlined />
                        </StatIcon>
                        <InfoStatLabel>Usage</InfoStatLabel>
                      </InfoStatHeader>
                      <InfoStatValue>
                        {`${templateData?.usedCount || 0} Times`}
                      </InfoStatValue>
                    </InfoStat>
                  </Col>
                </Row>
              </OverviewCard>

              <SectionTitle>Steps</SectionTitle>
              <div>
                {(templateData?.steps || []).map((step, index) => (
                  <StepItem key={step?.id || step?._id || index}>
                    <StepNumber>{index + 1}</StepNumber>
                    <StepContent onClick={() => playVideo(step)}>
                      <StepTitle>{getStepTitle(step)}</StepTitle>
                      {getStepMeta(step) && (
                        <StepMeta>{getStepMeta(step)}</StepMeta>
                      )}
                    </StepContent>
                  </StepItem>
                ))}
                {(!templateData?.steps ||
                  templateData?.steps?.length === 0) && (
                  <InfoValue>No steps available</InfoValue>
                )}
              </div>

              <FooterActions>
                <CustomSecondaryButton onClick={handleClose} size="large">
                  Close
                </CustomSecondaryButton>
                {onUseTemplate && (
                  <CustomButton onClick={onUseTemplate} size="large">
                    Use Template
                  </CustomButton>
                )}
              </FooterActions>
            </>
          ))}
        {!templateData && !loading && <InfoValue>No template data</InfoValue>}
      </ModalContent>
    </ModelWrapper>
    </>
  );
};

export default ViewTemplatesModal;
